/**
 * 主布局组件 — 左侧 Sidebar + 顶栏 Topbar + 主内容区 Outlet。
 *
 * Sidebar 按架构设计 §1.2 分组：总览 / 资产管理 / GEO分析 / 媒体投放 / 系统。
 * Topbar 包含：公司→品牌→产品三级级联选择器 + 当前用户信息 + 登出按钮。
 * 主内容区使用 React Router Outlet 渲染子路由页面。
 *
 * 多租户：公司用户 isLocked=true 时公司选择器 disabled；admin 可切换公司。
 * 页面刷新后：persist 恢复 selected ID，Layout 检测 companies 为空时重新加载。
 */
import { useState, useEffect, useCallback } from "react";
import { Outlet, useLocation, useNavigate, Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Select,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import {
  DashboardOutlined,
  Inventory2Outlined,
  AccountCircleOutlined,
  VisibilityOutlined,
  CompareArrowsOutlined,
  ArticleOutlined,
  CampaignOutlined,
  ReceiptLongOutlined,
  PostAddOutlined,
  SettingsOutlined,
  LogoutOutlined,
  PersonOutline,
  BusinessOutlined,
  LabelOutlined,
  InsightsOutlined,
  TuneOutlined,
  LinkOutlined,
  FactCheckOutlined,
  ContactMailOutlined,
  StorageOutlined,
} from "@mui/icons-material";
import { useAuthStore } from "../store/authStore";
import { useCompanyStore } from "../store/companyStore";
import { useSyncSelectionToUrl } from "../hooks/useSyncSelectionToUrl";
import { getCompanies } from "../api/companies";
import { getBrands } from "../api/brands";
import { getProducts } from "../api/products";
import { SIDEBAR_GROUPS } from "../utils/constants";
import type { Brand, ProductListItem } from "../types";
import fhsaiLogo from "../assets/fhsai-logo.png";

/** 侧边栏宽度。 */
const SIDEBAR_WIDTH = 220;

/** 路由路径到图标的映射。 */
const ROUTE_ICONS: Record<string, React.ReactNode> = {
  "/": <DashboardOutlined fontSize="small" />,
  "/products": <Inventory2Outlined fontSize="small" />,
  "/owned-accounts": <AccountCircleOutlined fontSize="small" />,
  "/ai-visibility": <VisibilityOutlined fontSize="small" />,
  "/competitive": <CompareArrowsOutlined fontSize="small" />,
  "/copy-library": <ArticleOutlined fontSize="small" />,
  "/generation": <PostAddOutlined fontSize="small" />,
  "/delivery-plans": <CampaignOutlined fontSize="small" />,
  "/expenses": <ReceiptLongOutlined fontSize="small" />,
  "/publish-records": <PostAddOutlined fontSize="small" />,
  "/platform-account": <SettingsOutlined fontSize="small" />,
  "/system-settings": <TuneOutlined fontSize="small" />,
  "/companies": <BusinessOutlined fontSize="small" />,
  "/brands": <LabelOutlined fontSize="small" />,
  "/media-insight": <InsightsOutlined fontSize="small" />,
  "/media-pool": <StorageOutlined fontSize="small" />,
  "/citation-sources": <LinkOutlined fontSize="small" />,
  "/index-ledger": <FactCheckOutlined fontSize="small" />,
  "/leads": <ContactMailOutlined fontSize="small" />,
};

/** 渲染侧边栏内容。 */
function SidebarContent({
  currentPath,
  isAdmin,
}: {
  currentPath: string;
  /** 二期批1 运营方 UI 差异：非 admin 过滤 adminOnly 平台级菜单。 */
  isAdmin: boolean;
}) {
  // 按角色过滤菜单：非 admin 隐藏 adminOnly 项；整组被滤空时组标题一并隐藏
  const visibleGroups = SIDEBAR_GROUPS.map((group) => ({
    ...group,
    items: isAdmin ? group.items : group.items.filter((item) => !item.adminOnly),
  })).filter((group) => group.items.length > 0);

  return (
    <Box sx={{ py: 1 }}>
      {/* Logo 区 */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          component="img"
          src={fhsaiLogo}
          alt="FHSAI"
          sx={{ height: 28 }}
        />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
          GEO 智能增长平台
        </Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />

      {/* 分组导航 */}
      <List dense disablePadding>
        {visibleGroups.map((group) => (
          <Box key={group.title} sx={{ mb: 0.5 }}>
            <Typography
              sx={{
                px: 2.5,
                py: 0.75,
                fontSize: 10,
                color: "text.disabled",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {group.title}
            </Typography>
            {group.items.map((item) => {
              const isActive =
                currentPath === item.path ||
                (item.path !== "/" && currentPath.startsWith(item.path));
              return (
                <ListItemButton
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  selected={isActive}
                  sx={{
                    py: 0.75,
                    px: 2.5,
                    mx: 1,
                    borderRadius: 1.5,
                    fontSize: 13,
                    color: isActive ? "primary.main" : "text.primary",
                    fontWeight: isActive ? 600 : 400,
                    bgcolor: isActive ? "brand.50" : "transparent",
                    "&:hover": {
                      bgcolor: isActive ? "brand.50" : "background.default",
                    },
                    "& .MuiListItemIcon-root": {
                      minWidth: 32,
                      color: isActive ? "primary.main" : "text.secondary",
                    },
                  }}
                >
                  <ListItemIcon>{ROUTE_ICONS[item.path]}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </ListItemButton>
              );
            })}
          </Box>
        ))}
      </List>
    </Box>
  );
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { account, logout } = useAuthStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // 选择态 URL 化同步层（URL 优先，详见 hook 注释）
  useSyncSelectionToUrl();

  // companyStore 状态和 actions
  const companies = useCompanyStore((s) => s.companies);
  const selectedCompanyId = useCompanyStore((s) => s.selectedCompanyId);
  const selectedBrandId = useCompanyStore((s) => s.selectedBrandId);
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);
  const isLocked = useCompanyStore((s) => s.isLocked);
  const setCompanies = useCompanyStore((s) => s.setCompanies);
  const selectCompany = useCompanyStore((s) => s.selectCompany);
  const selectBrand = useCompanyStore((s) => s.selectBrand);
  const selectProduct = useCompanyStore((s) => s.selectProduct);
  const initFromAccount = useCompanyStore((s) => s.initFromAccount);
  const resetCompanyStore = useCompanyStore((s) => s.reset);

  // 品牌和产品列表为 Layout 局部 state（不持久化，跟随公司/品牌选择动态加载）
  const [brands, setBrands] = useState<Brand[]>([]);
  const [products, setProducts] = useState<ProductListItem[]>([]);

  /** 处理登出：同时清除 authStore 和 companyStore。 */
  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    resetCompanyStore();
    navigate("/login");
  };

  /**
   * 页面刷新后的 companyStore 恢复逻辑。
   * 如果已登录但 companies 为空（persist 未持久化 companies），重新初始化。
   */
  useEffect(() => {
    if (!account) return;
    if (companies.length > 0) return;

    const restoreContext = async () => {
      // 根据账户角色初始化 isLocked
      initFromAccount(account);

      try {
        const companyList = await getCompanies();
        setCompanies(companyList);

        const switchable =
          account.role === "admin" || account.account_type === "operator";
        if (!switchable && account.company_id) {
          // 公司用户：锁定 account.company_id
          if (!selectedCompanyId || !companyList.some((c) => c.id === selectedCompanyId)) {
            selectCompany(account.company_id);
          }
        } else if (switchable) {
          // admin / 运营方：如果持久化的 selectedCompanyId 不在列表中，选第一个
          if (!selectedCompanyId || !companyList.some((c) => c.id === selectedCompanyId)) {
            if (companyList.length > 0) {
              // 默认选最后录入的（公司列表升序，最后=最新录入）
              selectCompany(companyList[companyList.length - 1].id);
            }
          }
        }
      } catch {
        // 静默处理，登录页会重新初始化
      }
    };

    restoreContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, companies.length]);

  /**
   * 监听 selectedCompanyId 变化 → 加载品牌列表。
   * 如果当前没有选中品牌（或品牌不属于该公司），自动选中第一个品牌。
   */
  const fetchBrands = useCallback(
    async (companyId: string) => {
      if (!companyId) {
        setBrands([]);
        return;
      }
      try {
        const brandList = await getBrands(companyId);
        setBrands(brandList);
        // 如果当前 selectedBrandId 为空或不属于该公司，自动选中第一个品牌
        if (
          brandList.length > 0 &&
          (!selectedBrandId || !brandList.some((b) => b.id === selectedBrandId))
        ) {
          // 默认选最后录入的（品牌列表升序，最后=最新录入）
          selectBrand(brandList[brandList.length - 1].id);
        }
      } catch {
        setBrands([]);
      }
    },
    [selectedBrandId, selectBrand],
  );

  useEffect(() => {
    if (selectedCompanyId) {
      fetchBrands(selectedCompanyId);
    } else {
      setBrands([]);
    }
  }, [selectedCompanyId, fetchBrands]);

  /**
   * 监听 selectedBrandId 变化 → 加载产品列表。
   * 如果当前没有选中产品（或产品不属于该品牌），自动选中最后添加的产品。
   */
  const fetchProducts = useCallback(
    async (brandId: string) => {
      if (!brandId) {
        setProducts([]);
        return;
      }
      try {
        const result = await getProducts({ brand_id: brandId, size: 100 });
        const productList = result.items;
        setProducts(productList);
        // 默认选中最后添加的产品（created_at 降序的第一个 = items[0]）
        // 注意：后端默认排序方向需确认，若为升序则改为 items[items.length - 1]
        if (
          productList.length > 0 &&
          (!selectedProductId || !productList.some((p) => p.id === selectedProductId))
        ) {
          selectProduct(productList[0].id);
        }
      } catch {
        setProducts([]);
      }
    },
    [selectedProductId, selectProduct],
  );

  useEffect(() => {
    if (selectedBrandId) {
      fetchProducts(selectedBrandId);
    } else {
      setProducts([]);
    }
  }, [selectedBrandId, fetchProducts]);

  /** 公司选择器变化：写 store + 同步写 URL query（replace，不污染历史）。 */
  const handleCompanyChange = (e: SelectChangeEvent<string>) => {
    const id = e.target.value;
    selectCompany(id);
    // 选择态 URL 化：公司切换后品牌/产品随级联重置，一并从 URL 清除
    const next = new URLSearchParams(searchParams);
    if (id) next.set("company", id);
    else next.delete("company");
    next.delete("brand");
    next.delete("product");
    setSearchParams(next, { replace: true });
  };

  /** 品牌选择器变化：写 store + 同步写 URL query。 */
  const handleBrandChange = (e: SelectChangeEvent<string>) => {
    const id = e.target.value;
    selectBrand(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set("brand", id);
    else next.delete("brand");
    next.delete("product");
    setSearchParams(next, { replace: true });
  };

  /** 产品选择器变化：写 store + 同步写 URL query。 */
  const handleProductChange = (e: SelectChangeEvent<string>) => {
    const id = e.target.value;
    selectProduct(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set("product", id);
    else next.delete("product");
    setSearchParams(next, { replace: true });
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* 左侧固定 Sidebar */}
      <Box
        component="nav"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          borderRight: "1px solid", borderColor: "divider",
          bgcolor: "background.paper",
          position: "fixed",
          height: "100vh",
          overflowY: "auto",
          zIndex: 1200,
        }}
      >
        <SidebarContent
          currentPath={location.pathname}
          isAdmin={account?.role === "admin"}
        />
      </Box>

      {/* 主区域 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: `${SIDEBAR_WIDTH}px`,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* 顶栏 */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: "background.paper",
            borderBottom: "1px solid", borderColor: "divider",
            color: "text.primary",
          }}
        >
          <Toolbar variant="dense" sx={{ minHeight: "48px !important", px: 3, gap: 1.5 }}>
            {/* 三级级联选择器：公司 → 品牌 → 产品 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* 公司选择器 */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  disabled={isLocked || companies.length === 0}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return <Typography sx={{ fontSize: 12, color: "text.disabled" }}>选择公司</Typography>;
                    const company = companies.find((c) => c.id === value);
                    return <Typography sx={{ fontSize: 12 }}>{company?.name || value}</Typography>;
                  }}
                  sx={{
                    fontSize: 12,
                    height: 32,
                    "& .MuiSelect-select": { py: 0.5, pr: 3 },
                  }}
                >
                  {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id} sx={{ fontSize: 12 }}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 品牌选择器 */}
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={selectedBrandId}
                  onChange={handleBrandChange}
                  disabled={!selectedCompanyId || brands.length === 0}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return <Typography sx={{ fontSize: 12, color: "text.disabled" }}>选择品牌</Typography>;
                    const brand = brands.find((b) => b.id === value);
                    return <Typography sx={{ fontSize: 12 }}>{brand?.name || value}</Typography>;
                  }}
                  sx={{
                    fontSize: 12,
                    height: 32,
                    "& .MuiSelect-select": { py: 0.5, pr: 3 },
                  }}
                >
                  {brands.map((b) => (
                    <MenuItem key={b.id} value={b.id} sx={{ fontSize: 12 }}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 产品选择器 */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedProductId}
                  onChange={handleProductChange}
                  disabled={!selectedBrandId || products.length === 0}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return <Typography sx={{ fontSize: 12, color: "text.disabled" }}>选择产品</Typography>;
                    const product = products.find((p) => p.id === value);
                    return <Typography sx={{ fontSize: 12 }}>{product?.name || value}</Typography>;
                  }}
                  sx={{
                    fontSize: 12,
                    height: 32,
                    "& .MuiSelect-select": { py: 0.5, pr: 3 },
                  }}
                >
                  {products.map((p) => (
                    <MenuItem key={p.id} value={p.id} sx={{ fontSize: 12 }}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {account?.role && (
                <Chip
                  label={account.role === "admin" ? "管理员" : "客户"}
                  size="small"
                  sx={{
                    fontSize: 11,
                    height: 20,
                    bgcolor: account.role === "admin" ? "brand.50" : "semanticBg.info",
                    color: account.role === "admin" ? "primary.main" : "info.main",
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ p: 0.5 }}
              >
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: "primary.main",
                    fontSize: 13,
                  }}
                >
                  {account?.account_name?.charAt(0) || "U"}
                </Avatar>
              </IconButton>
              <Typography sx={{ fontSize: 13, color: "text.primary" }}>
                {account?.account_name || "未登录"}
              </Typography>
              <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem disabled sx={{ fontSize: 13, minWidth: 160 }}>
                  <PersonOutline fontSize="small" sx={{ mr: 1 }} />
                  {account?.email || "—"}
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ fontSize: 13 }}>
                  <LogoutOutlined fontSize="small" sx={{ mr: 1 }} />
                  退出登录
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* 页面内容区 */}
        <Box sx={{ flexGrow: 1, p: 3, maxWidth: 1400, width: "100%", mx: "auto" }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
